import { type ReactNode, type SyntheticEvent } from "react";
import { Icon } from "src/components/Icon/Icon";
import { border_radius } from "src/design-system/tokens/border";
import ChevronRight from "src/design-system/icons/ChevronRight.svg?react";
import styled, { css } from "styled-components";
import { A11yOutline } from "src/utils/accessibility";
import { spacing } from "src/design-system/tokens/spacing";
import { font_size, font_weight } from "src/design-system/tokens/typography";
import { iconSize } from "src/design-system/tokens/iconSize";
import color from "src/design-system/tokens/color";

type IconAlign = "left" | "right";

type Props = {
  heading: string;
  tagline: string;
  icon: ReactNode;
  onClick: (event: SyntheticEvent) => void;
  iconAlign?: IconAlign;
  fullWidth?: boolean;
  outline?: boolean;
};

export function SegmentPromoDriveVariant({
  ref,
  heading,
  tagline,
  icon,
  onClick,
  iconAlign = "left",
  fullWidth = false,
  outline = false,
}: Props & {
  ref?: React.RefObject<HTMLButtonElement | null>;
}) {
  return (
    <TimelineCta
      ref={ref}
      iconAlign={iconAlign}
      fullWidth={fullWidth}
      outline={outline}
      onClick={onClick}
    >
      <IconWrapper>
        <Icon size="xl">{icon}</Icon>
      </IconWrapper>
      <span>
        <Heading>{heading}</Heading>
        <Tagline>
          {tagline}
          <ChevronWrapper>
            <ChevronRight
              width={iconSize.sm}
              height={iconSize.sm}
              color={color.icon.brand}
            />
          </ChevronWrapper>
        </Tagline>
      </span>
    </TimelineCta>
  );
}

const TimelineCta = styled.button<{
  iconAlign: IconAlign;
  fullWidth: boolean;
  outline: boolean;
}>`
  ${A11yOutline};
  display: flex;
  align-items: center;
  gap: ${spacing.lg};
  padding: ${({ outline }) => outline && spacing.md};
  background-color: ${color.primitive.neutral100};
  width: ${({ fullWidth }) => fullWidth && "100%"};
  cursor: pointer;

  &:hover {
    p {
      text-decoration: underline;
    }
  }

  ${({ iconAlign }) =>
    iconAlign === "left"
      ? `
    flex-direction: row;
    justify-content: left;
    text-align: left;
  `
      : `
    flex-direction: row-reverse;
    justify-content: right;
    text-align: right;
  `}

  ${({ outline }) =>
    outline &&
    `
      border: 1px solid ${color.primitive.neutral700};
      border-radius: ${border_radius.rounded_lg};
      padding: ${spacing.lg};
    `}
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background-color: ${color.primitive.neutral500};
  border-radius: ${border_radius.rounded_md};
`;
const ctaText = css`
  font-size: ${font_size.text_sm};
  font-weight: ${font_weight.medium};
`;

const Heading = styled.h3`
  ${ctaText}
  color: ${color.text.primary}
`;

const Tagline = styled.p`
  ${ctaText}
  display: inline-flex;
  align-items: center;
  color: ${color.text.brand};
`;

const ChevronWrapper = styled.span`
  margin-left: ${spacing.sm};
  display: flex;
  align-items: center;
  justify-content: center;
`;
